// US-012: Show waiting room when placed in lobby

import { useEffect, useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

export function WaitingRoom({
  meetingName,
  userName,
  onCancel,
  waitingFor = "admission",
}: {
  meetingName: string
  userName: string
  onCancel: () => void
  /**
   * `"host"`: "join at any time" is off and the host has not arrived, so there
   * is nobody to let anyone in yet — saying "once the host lets you in" was
   * wrong. `"admission"`: a waiting room or lock, where the host does admit.
   */
  waitingFor?: "host" | "admission"
}) {
  const t = useTranslation();
  const [dotCount, setDotCount] = useState(1)

  useEffect(() => {
    const t = setInterval(() => setDotCount((n) => (n >= 3 ? 1 : n + 1)), 600)
    return () => clearInterval(t)
  }, [])

  // All three dots are always laid out and the ones not yet shown are only
  // hidden, so the centred sentence keeps one width. Appending them as text
  // changed its width on every tick and slid the whole line sideways.
  const dots = (
    <span aria-hidden="true">
      {[1, 2, 3].map((n) => (
        <span key={n} className={n > dotCount ? "invisible" : undefined}>.</span>
      ))}
    </span>
  )

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-vc-surface px-4 text-vc-text">
      <div className="w-full max-w-md rounded-3xl bg-card/5 p-10 text-center ring-1 ring-white/10">
        {/* Animated waiting indicator */}
        <div className="relative mx-auto mb-6 h-20 w-20">
          <div className="absolute inset-0 animate-ping rounded-full bg-indigo-500/20" />
          <div className="absolute inset-2 animate-pulse rounded-full bg-indigo-500/30" />
          <div className="absolute inset-4 grid place-items-center rounded-full bg-indigo-500/40">
            <svg className="h-8 w-8 text-indigo-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        </div>

        {waitingFor === "host" ? (
          <>
            <h1 className="text-2xl font-bold text-white">{t("extraFiles.thanksForWaiting")}</h1>
            <p className="mt-2 text-sm text-white/60">
              {t("extraFiles.letYouKnowWhenHostReady")}{dots}
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold text-white">{t("extraFiles.waitingToBeAdmitted")}</h1>
            <p className="mt-2 text-sm text-white/60">
              {t("extraFiles.youllJoin")} <span className="font-semibold text-white">{meetingName}</span> {t("extraFiles.onceTheHostLetsYouIn")}{dots}
            </p>
          </>
        )}

        <div className="mt-6 rounded-2xl bg-card/5 px-4 py-3 ring-1 ring-white/10">
          <div className="text-xs text-white/50">{t("extraFiles.joiningAs")}</div>
          <div className="mt-1 font-semibold text-white">{userName}</div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-white/40">
          <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-card/40" style={{ animationDelay: "0ms" }} />
          <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-card/40" style={{ animationDelay: "150ms" }} />
          <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-card/40" style={{ animationDelay: "300ms" }} />
        </div>

        <button
          type="button"
          onClick={onCancel}
          className="mt-8 w-full rounded-xl bg-card/5 py-3 text-sm font-semibold text-white/80 ring-1 ring-white/10 transition hover:bg-card/10"
        >
          {t("extraFiles.leaveWaitingRoom")}
        </button>
      </div>
    </div>
  )
}
